import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, Search, User, Heart, ShoppingBag } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Logo } from "@/components/Logo";
import { useStore } from "@/context/StoreContext";
import { useCategories } from "@/hooks/useCategories";
import { SearchOverlay } from "./SearchOverlay";
import { SocialLinks } from "@/components/SocialLinks";
import { t } from "@/lib/i18n";

const IconBtn = ({ children, badge, testId, ...rest }) => (
  <button data-testid={testId} className="relative p-2 text-[#242321] hover:text-[#956842] transition-colors" {...rest}>
    {children}
    {badge > 0 && (
      <span data-testid={`${testId}-badge`} className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#956842] text-[#F5F0E8] text-[10px] font-bold flex items-center justify-center">
        {badge}
      </span>
    )}
  </button>
);

export const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { cartCount, favorites, setCartOpen } = useStore();
  const cats = useCategories();

  return (
    <header className="sticky top-0 z-50 bg-[#F5F0E8]/90 backdrop-blur-md border-b border-[#D5B98C]/40">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-8 h-[72px] flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <button data-testid="mobile-menu-button" className="lg:hidden p-2 -ml-2" onClick={() => setMenuOpen(true)} aria-label="Menu">
            <Menu className="w-5 h-5" />
          </button>
          <Logo />
        </div>
        <div className="flex items-center">
          <IconBtn testId="header-search-button" onClick={() => setSearchOpen(true)} aria-label={t("search")}><Search className="w-5 h-5" strokeWidth={1.5} /></IconBtn>
          <Link to="/account" className="hidden sm:block"><IconBtn testId="header-account-button" aria-label={t("account")}><User className="w-5 h-5" strokeWidth={1.5} /></IconBtn></Link>
          <Link to="/preferiti"><IconBtn testId="header-favorites-button" badge={favorites.length} aria-label={t("favorites")}><Heart className="w-5 h-5" strokeWidth={1.5} /></IconBtn></Link>
          <IconBtn testId="header-cart-button" badge={cartCount} onClick={() => setCartOpen(true)} aria-label={t("cart")}><ShoppingBag className="w-5 h-5" strokeWidth={1.5} /></IconBtn>
        </div>
      </div>
      <nav className="hidden lg:block border-t border-[#D5B98C]/30">
        <ul className="max-w-[1440px] mx-auto px-8 flex items-center gap-7 h-11 text-[12px] uppercase tracking-[0.16em] overflow-x-auto no-scrollbar">
          <li><NavLink to="/collezione" data-testid="nav-collection-link" className={({ isActive }) => `whitespace-nowrap hover:text-[#956842] transition-colors ${isActive ? "text-[#956842]" : ""}`}>{t("collection")}</NavLink></li>
          {cats.map((c) => (
            <li key={c.slug}>
              <NavLink to={`/categoria/${c.slug}`} data-testid={`nav-category-link-${c.slug}`} className={({ isActive }) => `whitespace-nowrap hover:text-[#956842] transition-colors ${isActive ? "text-[#956842]" : ""}`}>
                {c.name}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="left" className="bg-[#F5F0E8] w-[86vw] max-w-sm p-0" data-testid="mobile-menu">
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <div className="p-6 border-b border-[#D5B98C]/40"><Logo testId="mobile-menu-logo" /></div>
          <ul className="p-6 space-y-1">
            <li><Link onClick={() => setMenuOpen(false)} to="/collezione" data-testid="mobile-nav-collection" className="block py-2.5 font-serif text-2xl text-[#39251D]">Tutta la collezione</Link></li>
            {cats.map((c) => (
              <li key={c.slug}>
                <Link onClick={() => setMenuOpen(false)} to={`/categoria/${c.slug}`} data-testid={`mobile-nav-${c.slug}`} className="block py-2 text-[15px] text-[#242321] hover:text-[#956842]">{c.name}</Link>
              </li>
            ))}
          </ul>
          <div className="px-6 pt-4 pb-8 border-t border-[#D5B98C]/40 flex flex-col gap-3 text-sm">
            <Link onClick={() => setMenuOpen(false)} to="/account" data-testid="mobile-nav-account">Account</Link>
            <Link onClick={() => setMenuOpen(false)} to="/chi-siamo" data-testid="mobile-nav-about">Chi siamo</Link>
            <Link onClick={() => setMenuOpen(false)} to="/contatti" data-testid="mobile-nav-contacts">Contatti</Link>
            <SocialLinks className="mt-3 text-[#39251D]" prefix="mobile-social" />
          </div>
        </SheetContent>
      </Sheet>
      <SearchOverlay open={searchOpen} onOpenChange={setSearchOpen} />
    </header>
  );
};
